import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { StatusBadge, PriorityBadge } from '@/components/Badges';
import { EmptyState, LoadingState } from '@/components/States';
import Pagination from '@/components/Pagination';
import SLAIndicator from '@/components/SLAIndicator';
import { Plus, Search, Eye, Ticket as TicketIcon, Loader2, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { TICKET_STATUS_LABEL, PRIORITY_LABEL } from '@/lib/labels';
import { formatDate } from '@/lib/format';

const EMPTY_TICKET = {
  customerId: '', productId: '', categoryId: '', assignedToId: '',
  priority: 'MEDIUM', city: '', state: '', contactName: '', contactPhone: '',
  responsibleName: '', reason: 'Garantia', problemDescription: '',
};

export default function TicketsPage() {
  const { user, hasRole } = useAuth();
  const isSuperAdmin = hasRole('SUPER_ADMIN');
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ page: 1, pageSize: 10, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusF, setStatusF] = useState('all');
  const [priorityF, setPriorityF] = useState('all');
  const [openModal, setOpenModal] = useState(false);
  const [form, setForm] = useState(EMPTY_TICKET);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');

  const canCreate = hasRole('SUPER_ADMIN', 'ADMIN', 'SUPERVISOR', 'ATTENDANT');

  const load = useCallback(async (page = 1) => {
    if (isSuperAdmin && !selectedCompany) {
      setItems([]);
      setMeta({ page: 1, pageSize: 10, total: 0, totalPages: 0 });
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const params = { page, pageSize: 10 };
      if (q) params.q = q;
      if (statusF !== 'all') params.status = statusF;
      if (priorityF !== 'all') params.priority = priorityF;
      if (isSuperAdmin && selectedCompany) params.companyId = selectedCompany;
      const { data } = await api.get('/tickets', { params });
      setItems(data.items);
      setMeta({ page: data.page, pageSize: data.pageSize, total: data.total, totalPages: data.totalPages });
    } catch (err) {
      console.error('Failed to load tickets:', err);
      toast.error('Falha ao carregar tickets');
    }
    finally { setLoading(false); }
  }, [q, statusF, priorityF, isSuperAdmin, selectedCompany]);

  useEffect(() => {
    if (isSuperAdmin) {
      api.get('/companies', { params: { page: 1, pageSize: 1000 } })
        .then(({ data }) => setCompanies(data.items || []))
        .catch(() => toast.error('Erro ao carregar empresas'));
    }
  }, [isSuperAdmin]);

  useEffect(() => { load(1); }, [load]);

  useEffect(() => {
    if (isSuperAdmin && !selectedCompany) return;
    
    const companyParam = isSuperAdmin && selectedCompany ? { companyId: selectedCompany } : {};
    Promise.all([
      api.get('/customers', { params: { pageSize: 100 } }).catch((err) => { console.debug('customers load:', err?.message); return { data: { items: [] } }; }),
      api.get('/products', { params: { pageSize: 100 } }).catch((err) => { console.debug('products load:', err?.message); return { data: { items: [] } }; }),
      api.get('/categories', { params: { pageSize: 100 } }).catch((err) => { console.debug('categories load:', err?.message); return { data: { items: [] } }; }),
      api.get('/users', { params: { pageSize: 100 } }).catch((err) => { console.debug('users load:', err?.message); return { data: { items: [] } }; }),
    ]).then(([c, p, cat, u]) => {
      setCustomers(c.data.items);
      setProducts(p.data.items);
      setCategories(cat.data.items);
      setUsers(u.data.items);
    });
  }, []);

  const assignableUsers = useMemo(
    () => users.filter((u) => !u.roles?.includes('CLIENT')),
    [users]
  );

  const openCreate = () => { setForm(EMPTY_TICKET); setOpenModal(true); };

  const save = async () => {
    if (!form.customerId) return toast.error('Selecione um cliente');
    if (!form.problemDescription) return toast.error('Descreva o problema');
    setSaving(true);
    try {
      const { data } = await api.post('/tickets', form);
      toast.success(`Ticket ${data.protocol} criado`);
      setOpenModal(false);
      navigate(`/tickets/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><TicketIcon className="w-6 h-6 text-blue-600" /> Tickets</h1>
          <p className="text-sm text-slate-500">Chamados de garantia.</p>
        </div>
        {canCreate && <Button data-testid="new-ticket-btn" onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" disabled={isSuperAdmin && !selectedCompany}><Plus className="w-4 h-4 mr-2" /> Novo Ticket</Button>}
      </div>

      {isSuperAdmin && (
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Label className="text-sm font-medium whitespace-nowrap">Selecionar Empresa:</Label>
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger className="w-full max-w-md">
                <SelectValue placeholder="Selecione uma empresa..." />
              </SelectTrigger>
              <SelectContent>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.tradeName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>
      )}

      <Card className="border-slate-200">
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Buscar por protocolo, problema, contato..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
          </div>
          <Select value={statusF} onValueChange={setStatusF}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              {Object.entries(TICKET_STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={priorityF} onValueChange={setPriorityF}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas prioridades</SelectItem>
              {Object.entries(PRIORITY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? <LoadingState /> : items.length === 0 ? <EmptyState title="Nenhum ticket" icon={TicketIcon} /> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Protocolo</th>
                  <th className="px-4 py-3 font-medium">Cliente / Produto</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Prioridade</th>
                  <th className="px-4 py-3 font-medium">Responsável</th>
                  <th className="px-4 py-3 font-medium">SLA</th>
                  <th className="px-4 py-3 font-medium">Aberto em</th>
                  <th className="px-4 py-3 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3"><Link to={`/tickets/${t.id}${isSuperAdmin && selectedCompany ? `?companyId=${selectedCompany}` : ''}`} className="font-mono font-semibold text-blue-600 hover:underline">{t.protocol}</Link></td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{t.customer?.name || '-'}</div>
                      <div className="text-xs text-slate-500">{t.product?.name || '-'}</div>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                    <td className="px-4 py-3"><PriorityBadge priority={t.priority} /></td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{t.assignedTo?.name || '-'}</td>
                    <td className="px-4 py-3"><SLAIndicator ticket={t} /></td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(t.openedAt)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="icon" onClick={() => navigate(`/tickets/${t.id}${isSuperAdmin && selectedCompany ? `?companyId=${selectedCompany}` : ''}`)}><Eye className="w-4 h-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination {...meta} onPageChange={(p) => load(p)} />
      </Card>

      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Novo Ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Cliente *</Label>
              <Select value={form.customerId} onValueChange={(v) => setForm({ ...form, customerId: v })}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Produto</Label>
              <Select value={form.productId} onValueChange={(v) => setForm({ ...form, productId: v })}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} {p.model ? `(${p.model})` : ''}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Categoria</Label>
                <Select value={form.categoryId} onValueChange={(v) => {
                  const cat = categories.find(c => c.id === v);
                  setForm({ ...form, categoryId: v, priority: cat?.defaultPriority || form.priority });
                }}>
                  <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Prioridade *</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(PRIORITY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {hasRole('SUPER_ADMIN','ADMIN','SUPERVISOR') && (
              <div><Label>Atribuir responsável</Label>
                <Select value={form.assignedToId} onValueChange={(v) => setForm({ ...form, assignedToId: v })}>
                  <SelectTrigger><SelectValue placeholder="Sem atribuição" /></SelectTrigger>
                  <SelectContent>{assignableUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome do contato</Label><Input value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} /></div>
              <div><Label>Telefone</Label><Input value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} /></div>
            </div>
            <div><Label>Motivo</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} /></div>
            <div><Label>Descrição do problema *</Label><Textarea rows={4} value={form.problemDescription} onChange={(e) => setForm({ ...form, problemDescription: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando</> : 'Criar Ticket'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
